import { Component, ErrorInfo, ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  Collapse,
  Stack,
  Paper,
  Divider,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

// Wrapper component to use hooks in class component
function ErrorBoundaryContent({
  error,
  errorInfo,
  showDetails,
  onToggleDetails,
  onRetry,
  onReload,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  onToggleDetails: () => void;
  onRetry: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const isDevelopment = import.meta.env.DEV;

  const handleCopyError = () => {
    if (!error) return;
    const errorText = `${error.toString()}\n\n${error.stack || ""}\n\n${
      errorInfo?.componentStack || ""
    }`;
    navigator.clipboard.writeText(errorText).catch(() => {
      // Fallback if clipboard API fails
      const textArea = document.createElement("textarea");
      textArea.value = errorText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    });
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        padding: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "background.default",
        backgroundImage: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: 640,
          width: "100%",
          my: "auto",
          py: { xs: 2, sm: 3, md: 4 },
          animation: "fadeIn 0.5s ease-in",
          "@keyframes fadeIn": {
            from: {
              opacity: 0,
              transform: "translateY(-20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        {/* Main Error Card */}
        <Card
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            {/* Error Icon and Title */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "error.light",
                  color: "error.main",
                  flexShrink: 0,
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 40 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 0.5,
                  }}
                >
                  {t("errorBoundary.title")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("errorBoundary.subtitle")}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Error Message */}
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                mb: 4,
                lineHeight: 1.7,
              }}
            >
              {t("errorBoundary.message")}
            </Typography>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: isDevelopment && error ? 3 : 0 }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={onRetry}
                startIcon={<RefreshIcon />}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                }}
              >
                {t("errorBoundary.retry")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={onReload}
                startIcon={<RefreshIcon />}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                }}
              >
                {t("errorBoundary.reload")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleGoHome}
                startIcon={<HomeIcon />}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                }}
              >
                {t("errorBoundary.goHome")}
              </Button>
            </Stack>

            {/* Development Error Details */}
            {isDevelopment && error && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 2 }} />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {t("errorBoundary.details")}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={handleCopyError}
                      title={t("errorBoundary.copyError")}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={onToggleDetails}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      {showDetails ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Stack>
                </Stack>
                <Collapse in={showDetails}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      backgroundColor: "grey.50",
                      borderColor: "divider",
                      borderRadius: 2,
                      maxHeight: { xs: "50vh", sm: "60vh", md: "70vh" },
                      overflow: "auto",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.3)",
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {t("errorBoundary.errorMessage")}
                    </Typography>
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8125rem",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "text.primary",
                        mb: 3,
                        p: 2,
                        backgroundColor: "background.paper",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {error.toString()}
                      {error.stack && `\n\n${error.stack}`}
                    </Typography>
                    {errorInfo && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            color: "text.secondary",
                            mb: 1.5,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {t("errorBoundary.componentStack")}
                        </Typography>
                        <Typography
                          component="pre"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8125rem",
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            color: "text.primary",
                            p: 2,
                            backgroundColor: "background.paper",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {errorInfo.componentStack}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Collapse>
              </Box>
            )}

            {/* Help Text */}
            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                {t("errorBoundary.helpText")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleToggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorBoundaryContent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          showDetails={this.state.showDetails}
          onToggleDetails={this.handleToggleDetails}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundary(props: Props) {
  return <ErrorBoundaryClass {...props} />;
}
